import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";

export default function LoginPage() {
  const [tab, setTab]           = useState("staff");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const { login }               = useAuth();
  const navigate                = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Please enter email and password"); return; }
    setLoading(true); setError("");
    try {
      const endpoint = tab === "staff" ? "/auth/employee/login" : "/auth/customer/login";
      const res = await api.post(endpoint, { email, password });
      const d = res.data;
      if (d?.success && d?.data?.accessToken) {
        login(d.data, tab === "staff" ? "employee" : "customer");
        navigate(tab === "staff" ? "/portal" : "/customer");
      } else {
        setError(d?.message || "Login failed - check credentials");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const fill = (e, p, t) => { setTab(t); setEmail(e); setPassword(p); };

  // -- Styles (cream theme) --
  const NAVY       = "var(--navy, #0A1838)";
  const NAVY_DEEP  = "var(--navy-deep, #050D1A)";
  const AMBER      = "var(--amber, #F59E0B)";
  const CREAM      = "var(--cream, #FAF7F2)";
  const CREAM_WARM = "var(--cream-warm, #F1ECDF)";
  const PAPER      = "var(--paper, #FFFFFF)";
  const INK        = "var(--ink, #1F2937)";
  const MUTED      = "var(--muted, #6B7280)";
  const LINE       = "var(--line, #E5E7EB)";
  const SERIF      = 'var(--font-display, "DM Serif Display", Georgia, serif)';
  const SANS       = 'var(--font-body, "Inter Tight", -apple-system, sans-serif)';

  const S = {
    page:      { minHeight:"100vh", display:"grid", gridTemplateColumns:"1.1fr 1fr",
                 fontFamily:SANS, background:CREAM },
    left:      { padding:"60px 56px", display:"flex", flexDirection:"column",
                 justifyContent:"center", position:"relative", overflow:"hidden",
                 background:`linear-gradient(180deg, ${CREAM} 0%, ${CREAM_WARM} 100%)`,
                 borderRight:`1px solid ${LINE}` },
    orb:       { position:"absolute", top:"-200px", right:"-200px",
                 width:600, height:600,
                 background:"radial-gradient(circle, rgba(245,158,11,0.08), transparent 70%)",
                 pointerEvents:"none" },
    inner:     { position:"relative", zIndex:2, maxWidth:560 },
    logo:      { display:"flex", alignItems:"center", gap:10, marginBottom:48 },
    logoMark:  { fontSize:"1.4rem", color:AMBER, lineHeight:1 },
    logoName:  { fontFamily:SANS, fontSize:"1.05rem", fontWeight:700,
                 color:NAVY, letterSpacing:"-0.01em" },
    eyebrow:   { display:"inline-block", fontSize:"0.72rem",
                 letterSpacing:"0.15em", fontWeight:600,
                 color:AMBER, marginBottom:18, textTransform:"uppercase" },
    h1:        { fontFamily:SERIF, fontWeight:400,
                 fontSize:"clamp(2.2rem, 4.5vw, 3.6rem)",
                 lineHeight:1.05, letterSpacing:"-0.02em",
                 color:NAVY, marginBottom:24 },
    italic:    { fontStyle:"italic", color:AMBER },
    sub:       { fontSize:"1.05rem", color:MUTED,
                 lineHeight:1.6, marginBottom:36, maxWidth:520 },
    pills:     { display:"flex", flexWrap:"wrap", gap:8, marginBottom:40 },
    pill:      { padding:"6px 14px",
                 border:`1px solid ${LINE}`,
                 borderRadius:100, background:PAPER,
                 color:NAVY, fontSize:"0.78rem", fontWeight:500 },
    pillPlus:  { color:AMBER, marginRight:6, fontWeight:700 },
    stats:     { display:"flex", gap:40 },
    sNum:      { fontFamily:SERIF, fontSize:"2rem", fontWeight:400,
                 color:NAVY, lineHeight:1, letterSpacing:"-0.02em" },
    sLbl:      { fontSize:"0.72rem", color:MUTED, marginTop:6,
                 textTransform:"uppercase", letterSpacing:"0.08em",
                 fontWeight:600 },
    right:     { display:"flex", alignItems:"center", justifyContent:"center",
                 padding:"40px 32px", background:CREAM, overflowY:"auto" },
    wrap:      { width:"100%", maxWidth:420 },
    title:     { fontFamily:SERIF, fontSize:"2rem", fontWeight:400,
                 color:NAVY, marginBottom:8, letterSpacing:"-0.02em" },
    subtitle:  { fontSize:"0.95rem", color:MUTED,
                 lineHeight:1.5, marginBottom:28 },
    tabRow:    { display:"flex", background:CREAM_WARM,
                 borderRadius:10, padding:4, marginBottom:24, gap:4 },
    tabBtn:    { flex:1, padding:"10px 12px", borderRadius:8,
                 border:"none", background:"transparent",
                 color:MUTED, fontSize:"0.875rem", fontWeight:500,
                 cursor:"pointer", fontFamily:SANS, transition:"all 0.2s" },
    tabActive: { background:PAPER, color:NAVY,
                 boxShadow:"0 1px 2px rgba(10,24,56,0.04)" },
    errBox:    { display:"flex", alignItems:"center", gap:8, padding:"10px 14px",
                 background:"#FEE2E2", border:"1px solid #FECACA",
                 borderRadius:8, color:"#B91C1C", fontSize:"0.875rem", marginBottom:16 },
    label:     { display:"block", fontSize:"0.72rem", fontWeight:600,
                 color:MUTED, textTransform:"uppercase",
                 letterSpacing:"0.06em", marginBottom:6 },
    iWrap:     { position:"relative", display:"flex", alignItems:"center" },
    iIcon:     { position:"absolute", left:14, color:MUTED,
                 fontSize:14, lineHeight:1, pointerEvents:"none" },
    input:     { width:"100%", padding:"12px 14px 12px 40px",
                 background:PAPER, border:`1px solid ${LINE}`,
                 borderRadius:10, color:INK,
                 fontSize:"0.95rem", fontFamily:SANS,
                 outline:"none", minHeight:48,
                 transition:"border-color 0.2s, box-shadow 0.2s" },
    eyeBtn:    { position:"absolute", right:12, background:"none",
                 border:"none", cursor:"pointer", color:MUTED,
                 padding:4, display:"flex", alignItems:"center" },
    submit:    { width:"100%", padding:"14px 24px",
                 background:NAVY, color:PAPER, border:"none",
                 borderRadius:10, fontSize:"0.98rem", fontWeight:500,
                 cursor:"pointer", fontFamily:SANS, minHeight:50,
                 boxShadow:"0 4px 14px rgba(10,24,56,0.25)",
                 marginTop:4, transition:"background 0.2s, transform 0.2s" },
    track:     { textAlign:"center", marginTop:14, fontSize:"0.85rem", color:MUTED },
    qBox:      { marginTop:24, padding:18,
                 background:PAPER, border:`1px solid ${LINE}`,
                 borderRadius:12 },
    qTitle:    { fontSize:"0.68rem", fontWeight:700, color:AMBER,
                 textTransform:"uppercase", letterSpacing:"0.1em",
                 marginBottom:12 },
    qGrid:     { display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 },
    qBtn:      { padding:"12px 8px", background:CREAM,
                 border:`1px solid ${LINE}`, borderRadius:8,
                 cursor:"pointer", textAlign:"center",
                 fontFamily:SANS, color:INK,
                 transition:"all 0.2s" },
    qIcon:     { width:32, height:32, borderRadius:8,
                 background:"rgba(245,158,11,0.12)", color:AMBER,
                 display:"flex", alignItems:"center", justifyContent:"center",
                 fontFamily:SERIF, fontWeight:400, fontSize:"1rem",
                 margin:"0 auto 6px" },
    qLabel:    { fontSize:"0.78rem", fontWeight:600, color:NAVY, marginBottom:2 },
    qEmail:    { fontSize:"0.65rem", color:MUTED },
    footer:    { display:"flex", alignItems:"center", justifyContent:"center",
                 gap:10, marginTop:24, fontSize:"0.78rem", color:MUTED },
  };

  const EyeIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  );
  const EyeOffIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );

  return (
    <div style={S.page}>
      {/* -- LEFT: Hero panel -- */}
      <div style={S.left}>
        <div style={S.orb} />
        <div style={S.inner}>
          <div style={S.logo}>
            <span style={S.logoMark}>{'\u26A1'}</span>
            <span style={S.logoName}>CargoPulse</span>
          </div>

          <span style={S.eyebrow}>PAKISTAN'S MODERN LOGISTICS PLATFORM</span>
          <h1 style={S.h1}>
            Smart logistics,<br/>
            <em style={S.italic}>all in one platform.</em>
          </h1>
          <p style={S.sub}>
            Manage shipments, track fleets, handle finances and grow your logistics business with real-time intelligence.
          </p>

          <div style={S.pills}>
            {["Shipment Tracking","Fleet Management","Finance & COD","AI Analytics","Warehouse Ops","Live Dashboards"].map(f=>(
              <div key={f} style={S.pill}>
                <span style={S.pillPlus}>+</span>{f}
              </div>
            ))}
          </div>

          <div style={S.stats}>
            {[["12+","Modules"],["10","User Roles"],["99.9%","Uptime"],["AI","Powered"]].map(([n,l])=>(
              <div key={l}>
                <div style={S.sNum}>{n}</div>
                <div style={S.sLbl}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* -- RIGHT: Form panel -- */}
      <div style={S.right}>
        <div style={S.wrap}>
          <h2 style={S.title}>Sign in</h2>
          <p style={S.subtitle}>Access your logistics workspace and dashboards.</p>

          <div style={S.tabRow}>
            {[["staff","Staff / Admin"],["customer","Customer"]].map(([k,l])=>(
              <button key={k} onClick={()=>{setTab(k);setError("");}}
                style={{...S.tabBtn,...(tab===k?S.tabActive:{})}}>
                {l}
              </button>
            ))}
          </div>

          {error && <div style={S.errBox}><span>!</span> {error}</div>}

          <form onSubmit={handleSubmit} style={{display:"flex",flexDirection:"column",gap:16}}>
            <div>
              <label style={S.label}>Email Address</label>
              <div style={S.iWrap}>
                <span style={S.iIcon}>@</span>
                <input
                  type="email" value={email} onChange={e=>setEmail(e.target.value)}
                  placeholder={tab==="staff"?"admin@cargopulse.net":"customer@email.com"}
                  style={S.input} autoComplete="email" required
                />
              </div>
            </div>
            <div>
              <label style={S.label}>Password</label>
              <div style={S.iWrap}>
                <span style={S.iIcon}>{'\u2022'}</span>
                <input
                  type={showPass?"text":"password"} value={password}
                  onChange={e=>setPassword(e.target.value)} placeholder="Enter password"
                  style={{...S.input,paddingRight:44}}
                  autoComplete="current-password" required
                />
                <button type="button" onClick={()=>setShowPass(v=>!v)} style={S.eyeBtn} tabIndex={-1}>
                  {showPass ? <EyeOffIcon/> : <EyeIcon/>}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading}
              style={{...S.submit,opacity:loading?0.7:1}}>
              {loading?"Signing in...":"Sign in"}
            </button>
          </form>

          <div style={S.track}>
            <Link to="/track" style={{color:NAVY, textDecoration:"underline", textUnderlineOffset:3}}>
              Track a shipment without logging in
            </Link>
          </div>

          <div style={S.qBox}>
            <div style={S.qTitle}>Quick Access</div>
            <div style={S.qGrid}>
              <button style={S.qBtn} onClick={()=>fill("admin@cargopulse.net","Admin@123","staff")}>
                <div style={S.qIcon}>A</div>
                <div style={S.qLabel}>Admin</div>
                <div style={S.qEmail}>Full Access</div>
              </button>
              <button style={S.qBtn} onClick={()=>fill("staff@cargopulse.net","CP@123456","staff")}>
                <div style={{...S.qIcon, background:"rgba(59,130,246,0.12)", color:"#3B82F6"}}>S</div>
                <div style={S.qLabel}>Staff</div>
                <div style={S.qEmail}>Operations</div>
              </button>
              <button style={S.qBtn} onClick={()=>fill("customer@test.com","CP@123456","customer")}>
                <div style={{...S.qIcon, background:"rgba(20,184,166,0.12)", color:"#14B8A6"}}>C</div>
                <div style={S.qLabel}>Customer</div>
                <div style={S.qEmail}>Self-service</div>
              </button>
            </div>
          </div>

          <div style={S.footer}>
            <Link to="/" style={{color:NAVY, textDecoration:"underline", textUnderlineOffset:3}}>Back to homepage</Link>
            <span style={{color:LINE}}>|</span>
            <a href="mailto:info@axotech.net" style={{color:NAVY, textDecoration:"underline", textUnderlineOffset:3}}>Support</a>
          </div>
        </div>
      </div>
    </div>
  );
}
